/**
 * Custom hook to handle team ID initialization from location state or session storage
 * Manages team ID retrieval and authorization fetching
 * Clears selectedProjectId when team changes to prevent stale project data
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks";
import { setTeamId, fetchP2PAuthorization } from "../../store/slices/pathToPerformanceSlice";
import { clearSelectedProjectId } from "../../utils/teamDocumentsSessionStorage";
import { teamStorage } from "../../utils/teamStorage";

/**
 * Hook to initialize team ID from location state or session storage
 * Handles backward compatibility for both "teamId" and "teamID" keys
 * 
 * @returns void
 * 
 * @example
 * ```tsx
 * useTeamIdInitialization();
 * ```
 */
export const useTeamIdInitialization = (): void => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const initializedLocationKey = useRef<string | null>(null);

  useEffect(() => {
    if (initializedLocationKey.current === location.key) return;
    initializedLocationKey.current = location.key;

    // Try to get teamId from location state first
    // Handle both "teamId" and "teamID" for backward compatibility
    const stateTeamId = location.state?.teamId ?? location.state?.teamID;

    // If teamId is in location state, store it in sessionStorage
    if (stateTeamId) {
      const teamIdNum = typeof stateTeamId === "string"
        ? Number(stateTeamId)
        : stateTeamId;

      if (!Number.isInteger(teamIdNum) || teamIdNum <= 0) {
        dispatch(setTeamId(null));
        return;
      }
      
      // Check if team has changed
      const previousTeamId = teamStorage.getTeamId();
      if (previousTeamId !== String(teamIdNum)) {
        // Team changed - clear selected project
        clearSelectedProjectId();
      }
      
      teamStorage.setTeamId(teamIdNum);
      dispatch(setTeamId(teamIdNum));
      dispatch(fetchP2PAuthorization(teamIdNum));
      return;
    }

    // Fallback to localStorage if no state teamId
    const sessionTeamId = teamStorage.getTeamId();
    if (sessionTeamId) {
      const teamIdNum = Number(sessionTeamId);
      if (!Number.isInteger(teamIdNum) || teamIdNum <= 0) {
        dispatch(setTeamId(null));
        return;
      }
      dispatch(setTeamId(teamIdNum));
      dispatch(fetchP2PAuthorization(teamIdNum));
    } else {
      // No teamId found - set to null to trigger "Please select UBT" message
      dispatch(setTeamId(null));
    }
  }, [location.key, dispatch]);
};
